import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import apiService from '../services/api';
import '../styles/CookieConsent.css';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, user } = useUser();

  useEffect(() => {
    const checkCookieConsent = async () => {
      // Only show banner for authenticated users
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      // Wait a bit to ensure user is fully loaded
      await new Promise(resolve => setTimeout(resolve, 500));

      // Double-check authentication before making API call
      const token = apiService.getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Check user's cookie consent status from database
        const response = await apiService.getCookieConsent();
        
        // Show banner if:
        // 1. No response received
        // 2. cookieConsent is false or undefined (new users default to false)
        // 3. cookieConsent is null
        if (!response || response.cookieConsent === false || response.cookieConsent === null || response.cookieConsent === undefined) {
          // Show banner after a short delay for better UX
          setTimeout(() => setShowBanner(true), 1000);
        }
      } catch (error) {
        // If API fails, check the error type
        const errorMessage = error.message || error.toString() || '';
        
        if (errorMessage.includes('401') || errorMessage.includes('Unauthorized') || errorMessage.includes('Access token')) {
          // User not authenticated properly, don't show banner
          setLoading(false);
          return;
        }
        
        // For other errors (database column missing, network issues, etc.), 
        // show banner by default since we can't verify consent status
        console.error('Failed to check cookie consent, showing banner by default:', error);
        setTimeout(() => setShowBanner(true), 1000);
      } finally {
        setLoading(false);
      }
    };

    // Only run if authenticated and user exists
    if (isAuthenticated && user) {
      checkCookieConsent();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const acceptCookies = async () => {
    try {
      // Save consent to database
      await apiService.updateCookieConsent();
      setShowBanner(false);
    } catch (error) {
      console.error('Failed to save cookie consent:', error);
      // Hide banner anyway to avoid blocking user
      setShowBanner(false);
    }
  };

  if (loading || !showBanner) return null;

  return (
    <div className="cookie-consent-banner">
      <div className="cookie-consent-content">
        <div className="cookie-consent-text">
          <span className="cookie-icon">🍪</span>
          <div>
            <p style={{ marginBottom: '0.5rem' }}>
              We use essential cookies only to ensure the app works properly. These cookies are used for:
            </p>
            <ul style={{ fontSize: '0.9rem', marginTop: '0.5rem', marginLeft: '1.5rem', marginBottom: '0.5rem' }}>
              <li>Authentication (keeping you logged in)</li>
              <li>Session management</li>
              <li>Saving your preferences</li>
            </ul>
            <p style={{ fontSize: '0.85rem', fontStyle: 'italic', color: '#666' }}>
              We do not use tracking, analytics, or advertising cookies.
            </p>
          </div>
        </div>
        <div className="cookie-consent-buttons">
          <button className="cookie-btn cookie-btn-accept" onClick={acceptCookies}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;

